<template>
  <ion-app>
    <ion-content>
      <v-container fluid id="admin">
        <div class="van-hairline--bottom van-nav-bar" style="z-index: 1;" v-show="$vuetify.breakpoint.smAndDown">
          <div class="van-ellipsis van-nav-bar__title">{{ $t("admin.title") }}</div>
        </div>

        <v-tabs v-model="activeTab" class="background_color_ffffff">
          <v-tab>{{ $t("admin.members") }}</v-tab>
          <v-tab>{{ $t("admin.slideshow") }}</v-tab>
        </v-tabs>

        <!-- ============================================================ Members -->
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <div class="background_color_ffffff">
              <v-row justify="center" class="index_head padding_10">
                <v-col cols="10" sm="10" class="font_12">
                  <wc-search-box :placeholder="$t('admin.search')" @input="onClickSearch" />
                  <br />
                </v-col>
              </v-row>
            </div>

            <div class="pa-5">
              <v-data-iterator :items="memberList" :items-per-page="itemsPerPage" :page.sync="pageNo" hide-default-footer class="d-flex flex-column overflow-hidden">
                <template v-slot:no-data>
                  <div>Search for members by Email, or by name.</div>
                </template>

                <template v-slot:default="props">
                  <v-card xoutlined v-for="item in props.items" :key="item.MemberId">
                    <v-row class="text-caption text-sm-body-2 text-md-body-1" align="center">
                      <v-col cols="5" sm="5" md="4">{{ item.eMailAddress }}</v-col>
                      <v-col cols="4" sm="4" md="3">{{ item.Name }}</v-col>
                      <v-col cols="3" sm="3" md="3">{{ item.Phone }}</v-col>
                      <v-col cols="3" sm="3" md="2">
                        <v-btn color="warning" @click="onClickResetMember(item.MemberId)" v-show="item.OnHold">Reset</v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>

                <template v-slot:footer v-if="numberOfPages > 1">
                  <wc-list-footer v-model="pageNo" :maxPage="numberOfPages" />
                </template>
              </v-data-iterator>
            </div>
          </v-tab-item>

          <!-- ============================================================ Slideshow -->
          <v-tab-item>
            <div class="pa-5">
              <v-row class="mb-3" align="center" justify="space-between">
                <v-col cols="auto">
                  <v-btn color="primary" @click="onClickAddSlide">
                    <v-icon left>mdi-plus</v-icon>{{ $t("admin.slideshow.add") }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-data-iterator :items="slideshowList" hide-default-footer class="d-flex flex-column overflow-hidden">
                <template v-slot:no-data>
                  <div class="text-center grey--text py-8">{{ $t("admin.slideshow.empty") }}</div>
                </template>

                <template v-slot:default="props">
                  <v-row>
                    <v-col v-for="item in props.items" :key="item.Id" :cols="slideCols">
                      <v-card outlined class="slide-card">
                        <v-img :src="getImageUrl(item.ImagePath)" aspect-ratio="1.6" contain class="grey lighten-3"></v-img>
                        <v-card-text class="pa-2">
                          <!-- <div class="text-caption grey--text">{{ $t("admin.slideshow.id") }}: {{ item.Id }}</div> -->
                          <div class="text-caption grey--text text-truncate mt-1" :title="item.ImagePath">
                            {{ $t("admin.slideshow.path") }}: {{ item.ImagePath }}
                          </div>
                          <div>
                            <v-icon small :color="item.Visible ? 'green' : 'grey'">
                              {{ item.Visible ? 'mdi-eye' : 'mdi-eye-off' }}
                            </v-icon>
                            <span class="text-caption ml-1">{{ item.Visible ? $t("admin.slideshow.visible") : $t("admin.slideshow.hidden") }}</span>
                          </div>
                          <div class="text-caption grey--text">{{ $t("admin.slideshow.mtime") }}: {{ formatTime(item.LastModifiedStamp) }}</div>
                        </v-card-text>
                        <v-card-actions class="pa-2 pt-0">
                          <v-btn small color="primary" outlined @click="onClickEditSlide(item)">
                            <v-icon left small>mdi-pencil</v-icon>
                            <!-- {{ $t("admin.slideshow.edit") }} -->
                          </v-btn>
                          <v-spacer></v-spacer>
                          <v-btn small color="error" outlined class="ml-2" @click="onClickDeleteSlide(item)">
                            <v-icon left small>mdi-delete</v-icon>
                            <!-- {{ $t("admin.slideshow.delete") }} -->
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-data-iterator>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <!-- ============================================================ Slideshow edit dialog -->
        <v-dialog v-model="slideDialog" max-width="500">
          <v-card>
            <v-card-title class="headline">
              {{ editingSlide && editingSlide.Id ? $t("admin.slideshow.edit") : $t("admin.slideshow.add") }}
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field v-model="slideForm.ImagePath" :label="$t('admin.slideshow.image')" :placeholder="'https://...'" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-checkbox v-model="slideForm.Visible" :label="$t('admin.slideshow.visible')"></v-checkbox>
                  </v-col>
                  <v-col cols="12" v-if="slideForm.ImagePath">
                    <v-img :src="getImageUrl(slideForm.ImagePath)" aspect-ratio="2" contain class="grey lighten-3"></v-img>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="slideDialog = false">{{ $t("admin.slideshow.cancel") }}</v-btn>
              <v-btn color="primary" @click="onClickSaveSlide">{{ $t("admin.slideshow.save") }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- ============================================================ Slideshow delete confirm dialog -->
        <v-dialog v-model="deleteDialog" max-width="400">
          <v-card>
            <v-card-title class="headline">{{ $t("admin.slideshow.deleteTitle") }}</v-card-title>
            <v-card-text>{{ $t("admin.slideshow.deleteConfirm") }}</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="deleteDialog = false">{{ $t("admin.slideshow.cancel") }}</v-btn>
              <v-btn color="error" @click="onConfirmDeleteSlide">{{ $t("admin.slideshow.delete") }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </ion-content>
  </ion-app>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 0,
      onHoldOnly: true,
      searchText: "",
      curMember: null,
      memberList: [],

      pageNo: 1,

      slideshowList: [],
      slideDialog: false,
      editingSlide: null,
      slideForm: {
        Id: null,
        ImagePath: "",
        Visible: true,
      },
      deleteDialog: false,
      deleteTarget: null,
    };
  },
  computed: {
    member() {
      return this.$store.state.member;
    },
    itemsPerPage() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 99;
        case "sm":
          return 99;
        case "md":
          return 3 * 4;
        case "lg":
          return 3 * 6;
        case "xl":
          return 3 * 6;
      }
    },
    numberOfPages() {
      return Math.ceil(this.memberList.length / this.itemsPerPage);
    },
    // number of columns for the slideshow grid based on screen width
    slideCols() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 6; // 2 per row
        case "sm":
          return 4; // 3 per row
        case "md":
          return 3; // 4 per row
        case "lg":
          return 2; // 6 per row
        case "xl":
          return 2; // 6 per row
      }
    },
  },
  mounted() {
    if (this.member.AdminRole) {
      this.getSlideshowList();
    } else {
      this.$router.push({ name: "home" });
    }
  },
  methods: {
    // ------------------------------------------------------------------------ nextPage
    nextPage() {
      if (this.pageNo < this.numberOfPages) {
        this.pageNo += 1;
        this.$scrollTop();
      }
    },
    // ------------------------------------------------------------------------ prevPage
    prevPage() {
      if (this.pageNo > 1) {
        this.pageNo -= 1;
        this.$scrollTop();
      }
    },
    // ------------------------------------------------------------------------ onClickSearch
    onClickSearch(data) {
      this.searchText = data;
      this.getMemberList();
    },
    // ------------------------------------------------------------------------ getMemberList
    getMemberList() {
      this.MemberList = [];
      let obj = {
        AppVersion: this.$version,
        SessionId: this.member.SessionId,
        SessionToken: this.member.SessionToken,
        searchText: this.searchText,
      };
      this.$ajax2
        .post("SearchMember", obj, this.$ajaxHeader)
        .then((rpdata) => {
          if (rpdata.data.meta.code == "200") {
            this.memberList = rpdata.data.data;
          }
        }).catch(function (err) { }); // eslint-disable-line
    },
    // ------------------------------------------------------------------------ onClickResetMember
    onClickResetMember(memberId) {
      let obj = {
        AppVersion: this.$version,
        SessionId: this.member.SessionId,
        SessionToken: this.member.SessionToken,
        MemberId: memberId,
      };
      this.$ajax2
        .post("ResetMember", obj, this.$ajaxHeader)
        .then((rpdata) => {
          if (rpdata.data.meta.code == "200") {
            alert("reset successful");
            this.getMemberList();
          }
        }).catch(function (err) { }); // eslint-disable-line
    },

    // ============================================================ Slideshow
    // ------------------------------------------------------------------------ getSlideshowList
    getSlideshowList() {
      let obj = {
        AppVersion: this.$version,
        SessionId: this.member.SessionId,
        SessionToken: this.member.SessionToken,
      };
      this.$ajax2
        .post("GetSlideshowList", obj, this.$ajaxHeader)
        .then((rpdata) => {
          if (rpdata.data.meta.code == "200") {
            this.slideshowList = rpdata.data.data;
          }
        }).catch(function (err) { }); // eslint-disable-line
    },
    // ------------------------------------------------------------------------ onClickAddSlide
    onClickAddSlide() {
      this.editingSlide = null;
      this.slideForm = { Id: null, ImagePath: "", Visible: true };
      this.slideDialog = true;
    },
    // ------------------------------------------------------------------------ onClickEditSlide
    onClickEditSlide(item) {
      this.editingSlide = item;
      this.slideForm = {
        Id: item.Id,
        ImagePath: item.ImagePath,
        Visible: item.Visible,
      };
      this.slideDialog = true;
    },
    // ------------------------------------------------------------------------ onClickSaveSlide
    onClickSaveSlide() {
      if (!this.slideForm.ImagePath) {
        alert(this.$t("admin.slideshow.imageRequired"));
        return;
      }
      let obj = {
        AppVersion: this.$version,
        SessionId: this.member.SessionId,
        SessionToken: this.member.SessionToken,
        ImagePath: this.slideForm.ImagePath,
        Visible: this.slideForm.Visible,
      };
      if (this.slideForm.Id) {
        obj.Id = this.slideForm.Id;
        this.$ajax2
          .post("UpdateSlideshow", obj, this.$ajaxHeader)
          .then((rpdata) => {
            if (rpdata.data.meta.code == "200") {
              alert(this.$t("admin.slideshow.updateSuccess"));
              this.slideDialog = false;
              this.getSlideshowList();
            }
          }).catch(function (err) { }); // eslint-disable-line
      } else {
        this.$ajax2
          .post("AddSlideshow", obj, this.$ajaxHeader)
          .then((rpdata) => {
            if (rpdata.data.meta.code == "200") {
              alert(this.$t("admin.slideshow.addSuccess"));
              this.slideDialog = false;
              this.getSlideshowList();
            }
          }).catch(function (err) { }); // eslint-disable-line
      }
    },
    // ------------------------------------------------------------------------ onClickDeleteSlide
    onClickDeleteSlide(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    // ------------------------------------------------------------------------ onConfirmDeleteSlide
    onConfirmDeleteSlide() {
      if (!this.deleteTarget) return;
      let obj = {
        AppVersion: this.$version,
        SessionId: this.member.SessionId,
        SessionToken: this.member.SessionToken,
        Id: this.deleteTarget.Id,
      };
      this.$ajax2
        .post("DeleteSlideshow", obj, this.$ajaxHeader)
        .then((rpdata) => {
          if (rpdata.data.meta.code == "200") {
            alert(this.$t("admin.slideshow.deleteSuccess"));
            this.deleteDialog = false;
            this.deleteTarget = null;
            this.getSlideshowList();
          }
        }).catch(function (err) { }); // eslint-disable-line
    },
    // ------------------------------------------------------------------------ formatTime
    formatTime(ts) {
      if (!ts) return "";
      let d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString();
    },
    // ------------------------------------------------------------------------ getImageUrl
    // prepend the image path prefix for display (stored value stays relative)
    getImageUrl(path) {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) return path;
      return "https://baby.mrsdigi.com/files/" + path;
    },
  },
};
</script>

<style>
.slide-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.slide-card .v-card__text {
  flex: 1 1 auto;
}

.slide-card .v-card__actions {
  flex: 0 0 auto;
}

#brandList {
  width: 100%;
}

#brandList .brand_info {
  max-height: 100%;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  text-align: center;
  padding: 0px;
}

#brandList .brand_info .brand_title {
  font-size: 10px;
  text-align: center;
  width: 100%;
}

/* #brandList .brand_info .brand_image {
  border: 1px solid black;
} */
#brandList .brand_info .brand_image img {
  display: block;
  max-height: 100%;
  max-width: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
}
</style>
